import { useState, useEffect, useRef } from "react";
import { getAll } from "../services/firestoreService";
import { getCollectionVersions } from "../services/versionService";
import { getCache, setCache, getVersions, setVersion } from "../lib/cache";

export function useCollectionData(collectionName, fallbackData = []) {
  const [data, setData] = useState(fallbackData);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const load = async () => {
      try {
        // Step 1: Get server versions (1 Firestore read)
        const serverVersions = await getCollectionVersions();
        const serverVersion = serverVersions[collectionName];

        // Step 2: Get cached version + data
        const [cachedVersion, cachedData] = await Promise.all([
          getVersions().then((v) => v[collectionName]),
          getCache(collectionName),
        ]);

        // Step 3: If cache is fresh, use it immediately
        if (
          cachedData &&
          cachedVersion &&
          serverVersion &&
          serverVersion === cachedVersion
        ) {
          if (mountedRef.current && !cancelled) {
            setData(cachedData);
            setLoading(false);
          }
          return;
        }

        // Step 4: Cache miss or stale — fetch from Firestore
        const freshData = await getAll(collectionName);
        const result = freshData.length > 0 ? freshData : fallbackData;

        // Step 5: Update cache
        await Promise.all([
          setCache(collectionName, result),
          setVersion(
            collectionName,
            serverVersion || new Date().toISOString()
          ),
        ]);

        if (mountedRef.current && !cancelled) {
          setData(result);
          setLoading(false);
        }
      } catch {
        // Fallback: use static data on error
        if (mountedRef.current && !cancelled) {
          setData(fallbackData);
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName]);

  return { data, loading };
}
